/* eslint-disable react/forbid-prop-types */
/* eslint-disable no-param-reassign */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import * as d3 from 'd3';
import { scaleLinear } from 'd3-scale';
import { area } from 'd3-shape';
import { select } from 'd3-selection';
import { line } from 'd3';
import { axisLeft, axisBottom } from 'd3-axis';
import colors from '../../../../../../../../apps/baykis-pl/pattern-lab/_data/generated_colors.json';
import { getMovingAverage } from '../../../../../../tokens/js/utils/functions';
import { breakpoints } from '../../../../../../tokens/js/utils/vars/media';
import LegendFuture from './legendFuture.jsx';
import LegendPast from './legendPast.jsx';
import './graph.scss';

const calculationMinMax = (min, max, maxValue, minValue) => {
  maxValue = maxValue <= max ? max : maxValue;
  minValue = minValue >= min ? min : minValue;

  return {
    maxValue,
    minValue,
  };
};

const isSiteInDarkMod = () => {
  const el = document.querySelector('html[data-theme]');

  if (el) {
    const { theme } = el.dataset;

    if (theme) {
      return theme === 'dark';
    }
  }
  return false;
};

class Graph extends Component {
  constructor(props) {
    super(props);

    const { graphData, epoch } = this.props;

    this.createChart = this.createChart.bind(this);
    this.labelAxisYRef = React.createRef();
    this.placeholderChart = React.createRef();
    this.placeholderWrapper = React.createRef();
    this.graphData = [...graphData];
    this.epoch = epoch;

    this.themeSwitchHandler = this.themeSwitchHandler.bind(this);
    this.themeSwitcher = this.getThemeSwitcher();
    this.state = {
      chartSize: {
        width: 0,
        height: 0,
      },
      minValue: 0,
      maxValue: 0,
      minYAxis: 0,
      maxYAxis: 0,
      diffMaxMin: 0,
      siteInDarkMod: isSiteInDarkMod(),
    };

    this.winResize = this.winResize.bind(this);
    this.winResizeTimer = null;

    this.svg = d3.select('.svg');
  }

  static getRcpItemsByRcpFilter(rcpSelectedItems) {
    let rcp26Graph = null;
    let rcp45Graph = null;
    let rcp85Graph = null;

    if (rcpSelectedItems.length > 0) {
      rcp26Graph = rcpSelectedItems.find(
        (element) => element === 'rcp26_graph'
      );
      rcp45Graph = rcpSelectedItems.find(
        (element) => element === 'rcp45_graph'
      );
      rcp85Graph = rcpSelectedItems.find(
        (element) => element === 'rcp85_graph'
      );
    }

    return {
      rcp26Graph,
      rcp45Graph,
      rcp85Graph,
    };
  }

  componentDidMount() {
    window.addEventListener('resize', this.winResize);

    const chartSize = this.getChartSize();

    if (this.graphData.length > 0) {
      this.themeSwitcher.addEventListener();

      const { rcp } = this.props;
      const values = this.getMinMaxValues();

      this.setState({
        chartSize: {
          width: chartSize.width,
          height: chartSize.height,
        },
        minValue: values.minValue,
        maxValue: values.maxValue,
        minYAxis: values.minYAxis,
        maxYAxis: values.maxYAxis,
        diffMaxMin: values.diffMaxMin,
      });

      this.createChart(
        this.graphData,
        this.epoch,
        Graph.getRcpItemsByRcpFilter(rcp.selectedItems)
      );
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { graphData, epoch, rcp, chartTitle } = this.props;

    if (
      graphData.length > 0 &&
      (chartTitle !== prevProps.chartTitle ||
        (prevState.maxYAxis === 0 && prevState.maxValue === 0) ||
        rcp.selectedItems.join('') !== prevProps.rcp.selectedItems.join(''))
    ) {
      this.createChart(
        graphData,
        epoch,
        Graph.getRcpItemsByRcpFilter(rcp.selectedItems)
      );
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.winResize);

    this.themeSwitcher.removeEventListener();
  }

  getThemeSwitcher() {
    const items = document.querySelectorAll('.theme-switcher');

    return {
      addEventListener: () => {
        items.forEach((item) => {
          item.addEventListener('click', this.themeSwitchHandler);
        });
      },
      removeEventListener: () => {
        items.forEach((item) => {
          item.removeEventListener('click', this.themeSwitchHandler);
        });
      },
    };
  }

  getChartSize() {
    const placeholderWrapper = this.placeholderWrapper.current;
    const labelAxisYRef = this.labelAxisYRef.current;

    const { pdfView } = this.props;

    let marginTop = 0;
    let marginRight = 0;
    let marginBottom = 0;
    let marginLeft = 0;

    if (placeholderWrapper) {
      const svg = placeholderWrapper.querySelector('svg');

      if (svg) {
        const style = window.getComputedStyle(svg, null);

        marginTop = parseInt(style.marginTop, 10);
        marginRight = parseInt(style.marginRight, 10);
        marginBottom = parseInt(style.marginBottom, 10);
        marginLeft = parseInt(style.marginLeft, 10);
      }
    }

    let chartWidth = placeholderWrapper
      ? placeholderWrapper.offsetWidth - (marginLeft + marginRight)
      : null;
    chartWidth -= labelAxisYRef ? labelAxisYRef.clientWidth + 20 : 0;

    if (pdfView) {
      const vw = 750;
      chartWidth = vw - (marginLeft + marginRight - 100);
    }

    return {
      width: chartWidth,
      height: chartWidth
        ? Math.round(chartWidth * 0.5) - (marginTop + marginBottom)
        : null,
    };
  }

  getMinMaxValues() {
    let { minYAxis, maxYAxis, minValue, maxValue, diffMaxMin } = this.state;

    const { colorsets, graphData, epoch } = this.props;

    if (epoch === 'future') {
      graphData.forEach((element) => {
        element.forEach((el) => {
          const res = calculationMinMax(
            Math.floor(el.min),
            Math.ceil(el.max),
            maxValue,
            minValue
          );

          maxValue = res.maxValue;
          minValue = res.minValue;
        });
      });
      diffMaxMin = 0.2 * (maxValue - minValue);
      minYAxis = Math.floor(minValue - diffMaxMin);
      maxYAxis = Math.ceil(maxValue + diffMaxMin);
    } else if (colorsets && colorsets.length) {
      const [useColors] = colorsets;

      graphData.forEach((el, i) => {
        maxValue = el.value > maxValue ? el.value : maxValue;

        if (i === 0) {
          minValue = el.value;
        } else {
          minValue = minValue > el.value ? el.value : minValue;
        }
      });
      diffMaxMin = 0.2 * (Math.ceil(maxValue) - Math.floor(minValue));
      maxYAxis = Math.ceil(maxValue + diffMaxMin);

      minYAxis =
        useColors.absolutUnit === '°C' ||
        useColors.absolutUnit === 'Tag des Jahres'
          ? Math.floor(minValue - diffMaxMin)
          : 0;
    }

    return {
      minYAxis,
      maxYAxis,
      minValue,
      maxValue,
      diffMaxMin,
    };
  }

  getScale() {
    const { chartSize, minYAxis, maxYAxis } = this.state;
    const { epoch } = this.props;
    const graphDomain = epoch === 'past' ? [1950, 2020] : [1985, 2090];

    const xScale = scaleLinear()
      .domain(graphDomain)
      .range([0, chartSize.width]);

    const yScale = scaleLinear()
      .domain([minYAxis, maxYAxis])
      .range([chartSize.height, 0]);

    return {
      xScale,
      yScale,
    };
  }

  themeSwitchHandler() {
    const { rcp } = this.props;
    this.setState({ siteInDarkMod: isSiteInDarkMod() });

    this.createChart(
      this.graphData,
      this.epoch,
      Graph.getRcpItemsByRcpFilter(rcp.selectedItems)
    );
  }

  createChart(graphData, epoch, rcpItems) {
    const svgWrapper = select(this.placeholderChart.current);
    const { chartSize, minYAxis } = this.state;
    const { rcp26Graph, rcp45Graph, rcp85Graph } = rcpItems;
    const { colorsets } = this.props;
    const isMobileView = window.innerWidth <= breakpoints.phoneLarge;
    const isTabletView =
      window.innerWidth > breakpoints.phoneLarge &&
      window.innerWidth <= breakpoints.tablet;

    let radius = isTabletView ? 2 : 3;

    radius = isMobileView ? 1.5 : radius;

    if (colorsets !== undefined) {
      const { yScale, xScale } = this.getScale();

      xScale.range([0, chartSize.width]);
      yScale.range([chartSize.height, 0]);

      const yAxis = axisLeft()
        .tickSizeOuter(0)
        .tickFormat((domainValue) => `${domainValue}`.replace(/\./g, ','))
        .scale(yScale);
      const xAxis = axisBottom().tickFormat(d3.format('y')).scale(xScale);

      const isNullExistsInYAxis = minYAxis < 0;

      svgWrapper.selectAll('g').remove();
      svgWrapper.selectAll('path').remove();
      svgWrapper.selectAll('line').remove();
      svgWrapper.selectAll('text').remove();
      svgWrapper.selectAll('circle').remove();

      // x-Achse
      svgWrapper
        .append('g')
        .attr('class', 'x axis')
        .attr('transform', `translate(0, ${chartSize.height})`)
        .call(xAxis)
        .selectAll('text')
        .attr('y', 15)
        .attr('x', -14)
        .attr('dy', '.35em');

      // y-Achse
      svgWrapper.append('g').attr('class', 'y axis').call(yAxis).attr('y', -50);

      if (epoch === 'past') {
        const movingAverage = getMovingAverage(graphData);

        svgWrapper
          .selectAll('myCircles')
          .data(graphData)
          .enter()
          .append('circle')
          .attr('fill', colors.themeBase.addonYellow)
          .attr('stroke', 'none')
          .attr('cx', (d) => xScale(d.timestamp))
          .attr('cy', (d) => yScale(d.value))
          .attr('r', radius);

        svgWrapper
          .append('path')
          .datum(movingAverage)
          .attr('stroke', colors.themeBase.addonBrown)
          .attr('fill', 'none')
          .attr(
            'd',
            line()
              .x((d) => xScale(d.timestamp))
              .y((d) => yScale(d.value))
          );
      } else {
        const siteInDarkMod = isSiteInDarkMod();

        const arrayRCP26 = graphData[0];
        const arrayRCP45 = graphData[1];
        const arrayRCP85 = graphData[2];

        // ---RCP 2.6 -----
        svgWrapper
          .append('path')
          .datum(arrayRCP26)
          .attr(
            'fill',
            colors.themeBase.rcp26AreaWithAlphaParameter.replace(
              'alphaParameter',
              siteInDarkMod ? '.75' : '.7'
            )
          )
          .attr('class', 'rcp26_graph')
          .style('display', rcp26Graph ? 'block' : 'none')
          .attr(
            'd',
            area()
              .x((d) => xScale(d.timestamp))
              .y0((d) => yScale(d.min))
              .y1((d) => yScale(d.max))
          );

        svgWrapper
          .append('path')
          .datum(arrayRCP26)
          .attr('class', 'rcp26_graph')
          .attr(
            'stroke',
            colors.themeBase[`rcp26Line${siteInDarkMod ? 'DarkMode' : ''}`] ||
              colors.themeBase.rcp26Line
          )
          .attr('fill', 'none')
          .style('display', rcp26Graph ? 'block' : 'none')
          .style('stroke-width', siteInDarkMod ? 2 : 1)
          .attr(
            'd',
            line()
              .x((d) => xScale(d.timestamp))
              .y((d) => yScale(d.median))
          );

        // ---RCP 8.5 -----
        svgWrapper
          .append('path')
          .datum(arrayRCP85)
          .attr(
            'fill',
            colors.themeBase.rcp85AreaWithAlphaParameter.replace(
              'alphaParameter',
              siteInDarkMod ? '.6' : '.7'
            )
          )
          .attr('class', 'rcp85_graph')
          .style('display', rcp85Graph ? 'block' : 'none')
          .attr(
            'd',
            area()
              .x((d) => xScale(d.timestamp))
              .y0((d) => yScale(d.min))
              .y1((d) => yScale(d.max))
          );

        svgWrapper
          .append('path')
          .datum(arrayRCP85)
          .attr('class', 'rcp85_graph')
          .attr(
            'stroke',
            colors.themeBase[`rcp85Line${siteInDarkMod ? 'DarkMode' : ''}`] ||
              colors.themeBase.rcp85Line
          )
          .attr('fill', 'none')
          .style('display', rcp85Graph ? 'block' : 'none')
          .style('stroke-width', siteInDarkMod ? 2 : 1)
          .attr(
            'd',
            line()
              .x((d) => xScale(d.timestamp))
              .y((d) => yScale(d.median))
          );

        // ---RCP 4.5 -----
        svgWrapper
          .append('path')
          .datum(arrayRCP45)
          .attr(
            'fill',
            colors.themeBase.rcp45AreaWithAlphaParameter.replace(
              'alphaParameter',
              siteInDarkMod ? '.7' : '.25'
            )
          )
          .attr('class', 'rcp45_graph')
          .style('display', rcp45Graph ? 'block' : 'none')
          .attr(
            'd',
            area()
              .x((d) => xScale(d.timestamp))
              .y0((d) => yScale(d.min))
              .y1((d) => yScale(d.max))
          );

        svgWrapper
          .append('path')
          .datum(arrayRCP45)
          .attr('class', 'rcp45_graph')
          .attr(
            'stroke',
            colors.themeBase[`rcp45Line${siteInDarkMod ? 'DarkMode' : ''}`] ||
              colors.themeBase.rcp45Line
          )
          .attr('fill', 'none')
          .style('display', rcp45Graph ? 'block' : 'none')
          .style('stroke-width', siteInDarkMod ? 2 : 1)
          .attr(
            'd',
            line()
              .x((d) => xScale(d.timestamp))
              .y((d) => yScale(d.median))
          );
      }

      if (isNullExistsInYAxis) {
        svgWrapper
          .append('line')
          .attr('class', 'dottedLine')
          .attr('x1', 0)
          .attr('y1', yScale(0))
          .attr('x2', chartSize.width)
          .attr('y2', yScale(0));
      }
    }
  }

  winResize() {
    clearTimeout(this.winResizeTimer);

    this.winResizeTimer = setTimeout(() => {
      const { rcp } = this.props;
      const chartSize = this.getChartSize();

      this.setState({
        chartSize: {
          width: chartSize.width,
          height: chartSize.height,
        },
      });

      this.createChart(
        this.graphData,
        this.epoch,
        Graph.getRcpItemsByRcpFilter(rcp.selectedItems)
      );
    }, 150);
  }

  render() {
    const { siteInDarkMod } = this.state;
    const { chartSize } = this.state;
    const { rcp, chartTitle, chartSubtitle, epoch, unit, colorsets } =
      this.props;
    const rcpItems = Graph.getRcpItemsByRcpFilter(rcp.selectedItems);
    const legendItems = [
      {
        classes: `${rcpItems.rcp26Graph ? '' : ' hide'}`,
        text: 'RCP 2.6',
        medianColor:
          colors.themeBase[`rcp26Line${siteInDarkMod ? 'DarkMode' : ''}`] ||
          colors.themeBase.rcp26Line,
        bandWidthColor: colors.themeBase.rcp26AreaWithAlphaParameter.replace(
          'alphaParameter',
          siteInDarkMod ? '.75' : '.7'
        ),
      },
      {
        classes: `${rcpItems.rcp45Graph ? '' : ' hide'}`,
        text: 'RCP 4.5',
        medianColor:
          colors.themeBase[`rcp45Line${siteInDarkMod ? 'DarkMode' : ''}`] ||
          colors.themeBase.rcp45Line,
        bandWidthColor: colors.themeBase.rcp45AreaWithAlphaParameter.replace(
          'alphaParameter',
          siteInDarkMod ? '.7' : '.25'
        ),
      },
      {
        classes: `${rcpItems.rcp85Graph ? '' : ' hide'}`,
        text: 'RCP 8.5',
        medianColor:
          colors.themeBase[`rcp85Line${siteInDarkMod ? 'DarkMode' : ''}`] ||
          colors.themeBase.rcp85Line,
        bandWidthColor: colors.themeBase.rcp85AreaWithAlphaParameter.replace(
          'alphaParameter',
          siteInDarkMod ? '.6' : '.7'
        ),
      },
    ];

    const changeUnit =
      colorsets[0] !== undefined ? colorsets[0].changeUnit : '';
    const labelY =
      epoch === 'past'
        ? `${chartTitle} in ${unit}`
        : `Änderung zu 1971 - 2000 in ${changeUnit}`;

    return (
      <div
        ref={this.placeholderWrapper}
        className={`download-container chart-wrapper${rcp.viewIsActive ? ' rcp' : ''}`}
      >
        <h3 className="chart-title">{chartTitle}</h3>
        <h5 className="chart-subtitle">{chartSubtitle}</h5>
        <div className="svg-wrapper">
          <div
            ref={this.labelAxisYRef}
            className="labelingAxisY"
            style={{
              height: chartSize.height > 0 ? chartSize.height + 10 : '',
            }}
          >
            {labelY}
          </div>
          <svg
            className="svg"
            ref={this.placeholderChart}
            width={chartSize.width}
            height={chartSize.height}
          />
          <div className="labelingAxisX">Jahre</div>
        </div>
        {rcp.viewIsActive ? (
          <LegendFuture items={legendItems} />
        ) : (
          <LegendPast />
        )}
        <p className="copyright">
          ©&nbsp;Bayerisches Klimainformationssystem LfU&nbsp;
          {new Date().getFullYear()}
        </p>
      </div>
    );
  }
}

Graph.propTypes = {
  graphData: PropTypes.array,
  rcp: PropTypes.shape({
    viewIsActive: PropTypes.bool.isRequired,
    selectedItems: PropTypes.array.isRequired,
  }).isRequired,
  chartTitle: PropTypes.string,
  chartSubtitle: PropTypes.string,
  epoch: PropTypes.string,
  unit: PropTypes.string,
  colorsets: PropTypes.array,
  pdfView: PropTypes.bool,
};

Graph.defaultProps = {
  graphData: [],
  chartTitle: '',
  chartSubtitle: '',
  epoch: '',
  unit: '',
  colorsets: [],
  pdfView: false,
};

export default Graph;
